import React from 'react';
import { Link } from 'react-router-dom';
import { AppStoreBadge } from './AppStoreBadge';

const LEGAL = [
  { to: '/privacy', label: 'Privacy Policy' },
  { to: '/terms', label: 'Terms of Service' },
];

const COMPANY = [
  { to: '/blog', label: 'Blog' },
  { to: '/faq', label: 'FAQ' },
  { href: 'mailto:support@easytocook.com.au', label: 'Contact' },
  { to: '/signin', label: 'Admin' },
];

const SOCIAL = [
  {
    name: 'Instagram',
    href: 'https://www.instagram.com/easytocook_app',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
      </svg>
    ),
  },
  {
    name: 'TikTok',
    href: 'https://www.tiktok.com/@easytocook_app',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
      </svg>
    ),
  },
  {
    name: 'Facebook',
    href: 'https://www.facebook.com/profile.php?id=61585666516786',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
      </svg>
    ),
  },
];

const Footer: React.FC = () => {
  const year = new Date().getFullYear();
  const linkClass = 'text-[15px] text-ink-soft hover:text-ink transition-colors no-underline';

  return (
    <footer className="border-t-hairline bg-bg">
      <div className="max-w-7xl mx-auto px-5 md:px-6 py-12 md:py-14">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8">
          <div className="sm:col-span-2 lg:col-span-6">
            <Link to="/" className="inline-flex no-underline">
              <img src="/images/EASYTOCOOK.png" alt="EasyToCook" className="h-14 object-contain" />
            </Link>
            <p className="mt-4 mb-3 text-[15px] text-ink-soft">Download EasyToCook</p>
            <AppStoreBadge size="sm" />
          </div>

          <div className="lg:col-span-3">
            <h4 className="text-[15px] font-display font-bold text-ink mb-4">Legal</h4>
            <ul className="flex flex-col gap-3 list-none p-0 m-0">
              {LEGAL.map((l) => (
                <li key={l.label}>
                  <Link to={l.to} className={linkClass}>
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h4 className="text-[15px] font-display font-bold text-ink mb-4">Company</h4>
            <ul className="flex flex-col gap-3 list-none p-0 m-0">
              {COMPANY.map((l) => (
                <li key={l.label}>
                  {l.to ? (
                    <Link to={l.to} className={linkClass}>
                      {l.label}
                    </Link>
                  ) : (
                    <a href={l.href} className={linkClass}>
                      {l.label}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t-hairline flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="m-0 text-body-sm text-ink-muted">© {year} EasyToCook. All rights reserved.</p>
          <div className="flex items-center gap-2">
            {SOCIAL.map((s) => (
              <a
                key={s.name}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.name}
                className="w-10 h-10 rounded-full flex items-center justify-center text-ink-soft hover:text-ink hover:bg-surface-sunk transition-colors"
              >
                {s.icon}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
